import { Box, Button, ButtonGroup, Card, Divider, HorizontalStack, Page, Tabs, Text, VerticalStack } from '@shopify/polaris';
import { useState } from 'react';
import { useCurrency } from '../../../customHooks/useCurrency';
import { useTimezone } from '../../../customHooks/useTimezone';
import HeadSection from '../../UI/Reports/HeadSection';
import TotalizeNumbers from '../../UI/Reports/TotalizeNumbers';
import { VerticalSpace } from '../../UI/Reports/VerticalSpace';
import { ActiveFilterChips } from './efficacy/ActiveFilterChips';
import { formatMoney, formatPct, formatUnits } from './efficacy/formatters';
import { ExportMenu } from './replenishment/ExportMenu';
import { FiltersCard } from './replenishment/FiltersCard';
import { MonthlyTrendChart } from './replenishment/MonthlyTrendChart';
import { ReplenishmentHighlights } from './replenishment/ReplenishmentHighlights';
import { SeriesTable } from './replenishment/SeriesTable';
import { useReplenishmentData } from './replenishment/useReplenishmentData';
import { useReplenishmentFilters } from './replenishment/useReplenishmentFilters';
import { formatRangeLabel } from './replenishment/constants';

const TABS = [
  { id: 'series', content: 'Análisis por Series' },
  { id: 'category', content: 'Análisis por Categoría' },
  { id: 'warehouse', content: 'Estado de Almacén' },
  { id: 'monthly', content: 'Evolución mensual' },
];

/**
 * Report de Reposición — análisis de productos `season = 'BASICO'` sobre
 * un rango de fechas configurable con comparativa period-over-period.
 *
 * Composición (mismo patrón que ViewReportEfficacy):
 *   - FiltersCard (filtros, fuera del Card principal)
 *   - HeadSection (título + subtítulo)
 *   - Card con: Tabs + ActiveFilterChips + TotalizeNumbers (KPIs globales)
 *   - Contenido por tab (Series · Categoría · Almacén · Evolución)
 */
const ViewReportReplenishment = () => {
  const { currency } = useCurrency();
  // eslint-disable-next-line no-unused-vars
  const { timezone } = useTimezone();
  const fmt$ = (n) => formatMoney(n, currency);

  const filterState = useReplenishmentFilters();
  const { data, monthly, sortBy, sortDir, handleSort } = useReplenishmentData(filterState);
  const rows = data?.data || [];
  const [trendMode, setTrendMode] = useState('units');
  const [tabIdx, setTabIdx] = useState(0);

  const stats = data?.metadata;
  const rangeLabel = formatRangeLabel(filterState.range.from, filterState.range.toExclusive);

  const rentabilidad =
    stats && stats.totalBuyValue > 0
      ? (stats.totalSellValuePvf - stats.totalBuyValue) / stats.totalBuyValue
      : null;

  // KPIs del strip de cabecera — los más representativos del período en
  // su conjunto. Δ stock y ratio reposición son los que distinguen este
  // informe del de Eficacia y por eso se elevan al strip.
  const kpiRows = stats
    ? [
        { label: 'Vendido', value: `${formatUnits(stats.totalSellUnits)} Uds.` },
        {
          label: 'Δ Stock (uds)',
          value: `${stats.totalStockDeltaUnits >= 0 ? '+' : ''}${formatUnits(stats.totalStockDeltaUnits)}`,
          hint: stats.totalStockDeltaUnits >= 0 ? 'Almacén crece' : 'Almacén decae',
        },
        {
          label: 'Ratio reposición',
          value: stats.globalReplenishmentRatio != null ? stats.globalReplenishmentRatio.toFixed(2) : '—',
          hint: 'Compras ÷ ventas',
        },
        {
          label: 'Rotación (anual)',
          value: stats.globalRotation != null ? `${stats.globalRotation.toFixed(2)}×` : '—',
        },
        {
          label: 'Cobertura',
          value: stats.globalCoverage != null ? `${formatUnits(Math.round(stats.globalCoverage))} días` : '—',
        },
        {
          label: 'Rentabilidad',
          value: rentabilidad != null ? formatPct(rentabilidad) : '—',
        },
      ]
    : [];

  return (
    <Page
      fullWidth
      title="Reposición"
      subtitle="Análisis de productos básicos (reposición continua) por período"
      primaryAction={
        <ExportMenu
          disabled={!data}
          range={filterState.range}
          currency={currency}
          exportParamsArgs={{
            validFilters: filterState.validFilters,
            depositsValue: filterState.depositsValue,
            depositRolesValue: filterState.depositRolesValue,
            depositGroupsValue: filterState.depositGroupsValue,
            familyQuery: filterState.familyQuery,
          }}
        />
      }
    >
      <FiltersCard
        presetId={filterState.presetId}
        setPresetId={filterState.setPresetId}
        range={filterState.range}
        setCustomRange={filterState.setCustomRange}
        depositsValue={filterState.depositsValue}
        depositRolesValue={filterState.depositRolesValue}
        depositGroupsValue={filterState.depositGroupsValue}
        setDepositScope={filterState.setDepositScope}
        depositOptions={filterState.depositOptions}
        groupOptions={filterState.groupOptions}
        filters={filterState.filters}
        handleFilter={filterState.handleFilter}
        clearFilters={filterState.clearFilters}
        familySearch={filterState.familySearch}
        setFamilySearch={filterState.setFamilySearch}
      />

      {data && (
        <>
          <VerticalSpace size="large" />
          <HeadSection
            title={rangeLabel}
            subtitle={`${formatUnits(stats?.count ?? 0)} series · ${stats?.periodDays ?? 0} días de período`}
          />
          <VerticalSpace />
          <Card>
            <Tabs tabs={TABS} selected={tabIdx} onSelect={setTabIdx} />
            <Divider />
            <Box padding="4">
              <ActiveFilterChips
                filters={filterState.filters}
                depositsValue={filterState.depositsValue}
                depositRolesValue={filterState.depositRolesValue}
                depositGroupsValue={filterState.depositGroupsValue}
                depositOptions={filterState.depositOptions}
                groupOptions={filterState.groupOptions}
                familyQuery={filterState.familyQuery}
              />
              <Box minHeight=".75rem" />
              <TotalizeNumbers rows={kpiRows} />
            </Box>
          </Card>

          {tabIdx === 0 && (
            <>
              <Box minHeight=".5rem" />
              <Card padding="0">
                <Box padding="4">
                  <Text variant="headingMd" as="h3">
                    Detalle por serie
                  </Text>
                  <Box minHeight=".25rem" />
                  <Text variant="bodySm" tone="subdued">
                    {`${formatUnits(stats?.totalBuyUnits ?? 0)} uds compradas · ${formatUnits(
                      stats?.totalSellUnits ?? 0,
                    )} uds vendidas · ${formatUnits(stats?.totalRemainUnits ?? 0)} uds en almacén`}
                  </Text>
                </Box>
                <SeriesTable
                  rows={rows}
                  currency={currency}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </Card>
              {rows.length > 0 && (
                <>
                  <Box minHeight=".5rem" />
                  <Card>
                    <Box padding="4">
                      <Text variant="headingMd" as="h3">
                        Series destacadas
                      </Text>
                      <Box minHeight=".25rem" />
                      <Text variant="bodySm" tone="subdued">
                        Las series con mayor y menor rotación del período — extremos del catálogo BASICO.
                      </Text>
                      <Box padding="8">
                        <ReplenishmentHighlights rows={rows} currency={currency} />
                      </Box>
                    </Box>
                  </Card>
                </>
              )}
            </>
          )}

          {tabIdx === 1 && (
            <>
              <Box minHeight=".5rem" />
              <Card>
                <Box padding="4">
                  <Text variant="headingMd" as="h3">
                    Análisis por Categoría
                  </Text>
                  <Box minHeight=".5rem" />
                  <Text variant="bodySm" tone="subdued">
                    Próximamente: breakdown por categoría con métricas, radar de rotación y bar chart de
                    sobre/subcompra.
                  </Text>
                </Box>
              </Card>
            </>
          )}

          {tabIdx === 2 && (
            <>
              <Box minHeight=".5rem" />
              <Card>
                <Box padding="4">
                  <Text variant="headingMd" as="h3">
                    Estado de Almacén
                  </Text>
                  <Box minHeight=".5rem" />
                  <Text variant="bodySm" tone="subdued">
                    Próximamente: foto inicio/fin de almacén, ratio de reposición con semáforo, productos hot
                    (riesgo stockout) y zombi (capital muerto).
                  </Text>
                  {stats && (
                    <>
                      <Box minHeight="1rem" />
                      <HorizontalStack gap="8" wrap>
                        <KpiPreview label="Stock inicio (uds)" value={formatUnits(stats.totalStockStartUnits)} />
                        <KpiPreview label="Stock fin (uds)" value={formatUnits(stats.totalStockEndUnits)} />
                        <KpiPreview
                          label="Δ uds"
                          value={`${stats.totalStockDeltaUnits >= 0 ? '+' : ''}${formatUnits(
                            stats.totalStockDeltaUnits,
                          )}`}
                          tone={stats.totalStockDeltaUnits >= 0 ? 'success' : 'critical'}
                        />
                        <KpiPreview label="Stock inicio (€)" value={fmt$(stats.totalStockStartValue)} />
                        <KpiPreview label="Stock fin (€)" value={fmt$(stats.totalStockEndValue)} />
                        <KpiPreview
                          label="Δ valor"
                          value={`${stats.totalStockDeltaValue >= 0 ? '+' : ''}${fmt$(stats.totalStockDeltaValue)}`}
                          tone={stats.totalStockDeltaValue >= 0 ? 'success' : 'critical'}
                        />
                      </HorizontalStack>
                    </>
                  )}
                </Box>
              </Card>
            </>
          )}

          {tabIdx === 3 && monthly && (
            <>
              <Box minHeight=".5rem" />
              <Card>
                <Box padding="4">
                  <HorizontalStack align="space-between" blockAlign="center">
                    <VerticalStack gap="05">
                      <Text variant="headingMd" as="h3">
                        Evolución mensual de ventas
                      </Text>
                      <Text variant="bodySm" tone="subdued" as="span">
                        Últimos {monthly.months.length} meses · top 6 categorías por volumen
                      </Text>
                    </VerticalStack>
                    <ButtonGroup segmented>
                      <Button primary={trendMode === 'units'} onClick={() => setTrendMode('units')}>
                        Unidades
                      </Button>
                      <Button primary={trendMode === 'money'} onClick={() => setTrendMode('money')}>
                        Valor (PVF)
                      </Button>
                    </ButtonGroup>
                  </HorizontalStack>
                  <Box minHeight=".75rem" />
                  <MonthlyTrendChart monthly={monthly} mode={trendMode} />
                </Box>
              </Card>
            </>
          )}

          <VerticalSpace size="xx-large" />
        </>
      )}
    </Page>
  );
};

// KPI preview compacto (sólo usado en la tab Almacén mientras la
// versión final con todos los componentes está pendiente).
const KpiPreview = ({ label, value, tone }) => (
  <VerticalStack gap="05">
    <Text variant="bodySm" tone="subdued" as="span">
      {label.toUpperCase()}
    </Text>
    <Text variant="headingLg" tone={tone || 'default'} as="span">
      {value}
    </Text>
  </VerticalStack>
);

export default ViewReportReplenishment;
